import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import {
  deleteOrganization,
  fetchCurrentUser,
  updateOrganization,
} from "./usersSlice";

export const deepSet = (obj, path, val) => {
  path = path.replaceAll("[", ".[");
  const keys = path.split(".");

  for (let i = 0; i < keys.length; i++) {
    let currentKey = keys[i];
    let nextKey = keys[i + 1];
    if (currentKey.includes("[")) {
      currentKey = parseInt(currentKey.substring(1, currentKey.length - 1));
    }
    if (nextKey && nextKey.includes("[")) {
      nextKey = parseInt(nextKey.substring(1, nextKey.length - 1));
    }

    if (typeof nextKey !== "undefined") {
      obj[currentKey] = obj[currentKey]
        ? obj[currentKey]
        : isNaN(nextKey)
          ? {}
          : [];
    } else {
      obj[currentKey] = val;
    }

    obj = obj[currentKey];
  }
};

export const deepDelete = (obj, path) => {
  path = path.replaceAll("[", ".[");
  const keys = path.split(".");

  for (let i = 0; i < keys.length; i++) {
    let currentKey = keys[i];
    let nextKey = keys[i + 1];
    if (currentKey.includes("[")) {
      currentKey = parseInt(currentKey.substring(1, currentKey.length - 1));
    }
    if (nextKey && nextKey.includes("[")) {
      nextKey = parseInt(nextKey.substring(1, nextKey.length - 1));
    }

    if (typeof nextKey !== "undefined") {
      obj[currentKey] = obj[currentKey]
        ? obj[currentKey]
        : isNaN(nextKey)
          ? {}
          : [];
    } else {
      delete obj[currentKey];
    }

    obj = obj[currentKey];
  }
};

// Thunks
export const fetchProjects = createAsyncThunk(
  "projects/fetchProjects",
  async ({ organizationId, isDeleted = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/project?isDeleted=${isDeleted}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectsForAccess = createAsyncThunk(
  "projects/fetchProjectsForAccess",
  async ({ organizationId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/organization/${organizationId}/project?isDeleted=false`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchSharedProjects = createAsyncThunk(
  "projects/fetchSharedProjects",
  async ({ organizationId, isDeleted = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/me/share?isDeleted=${isDeleted}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectUsers = createAsyncThunk(
  "projects/fetchProjectUsers",
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}/role`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateProjectNote = createAsyncThunk(
  "projects/updateProjectNote",
  async ({ projectId, releaseNote }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/v1/project/${projectId}/note`,
        { releaseNote }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateVersionNote = createAsyncThunk(
  "projects/updateVersionNote",
  async ({ versionId, releaseNote }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/v1/version/${versionId}/note`,
        { releaseNote }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateProjectArchiveStatus = createAsyncThunk(
  "projects/updateProjectArchiveStatus",
  async ({ projectId, isArchived }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/v1/project/${projectId}?isDeleted=${isArchived}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateVersionArchiveStatus = createAsyncThunk(
  "projects/updateVersionArchiveStatus",
  async ({ versionId, isArchived }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/v1/version/${versionId}?isDeleted=${isArchived}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectById = createAsyncThunk(
  "projects/fetchProjectById",
  async (
    { projectId, showArchived = false, isLoggedShared = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}?isDeleted=${showArchived}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectByIdForAccess = createAsyncThunk(
  "projects/fetchProjectByIdForAccess",
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}?isDeleted=false`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchSubprojectsForProject = createAsyncThunk(
  "projects/fetchSubprojectsForProject",
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}/subproject`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchSharedProjectById = createAsyncThunk(
  "projects/fetchSharedProjectById",
  async ({ projectId, showArchived = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}?isDeleted=${showArchived}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchProjectByIdForExplorer = createAsyncThunk(
  "projects/fetchProjectByIdForExplorer",
  async (
    { projectId, showArchived = false, isLoggedShared = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/project/${projectId}?isDeleted=${showArchived}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createProject = createAsyncThunk(
  "projects/createProject",
  async (projectData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/api/v1/project",
        projectData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          transformRequest: [(data) => data], // Prevent axios from transforming FormData
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const addUserToProject = createAsyncThunk(
  "projects/addUserToProject",
  async ({ projectId, userData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/project/${projectId}/role`,
        userData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const removeUserFromProject = createAsyncThunk(
  "projects/removeUserFromProject",
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/project/${projectId}/role/${userId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createVersion = createAsyncThunk(
  "projects/createVersion",
  async ({ projectId, versionData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/project/${projectId}/version`,
        versionData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getVersionUploadData = createAsyncThunk(
  "projects/getVersionUploadData",
  async (versionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/version/${versionId}/upload`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createOrganization = createAsyncThunk(
  "projects/createOrganization",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/v1/organization`, data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchVersion = createAsyncThunk(
  "projects/fetchVersion",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/v1/version/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateVersion = createAsyncThunk(
  "projects/updateVersion",
  async ({ versionId, title, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/v1/version/${versionId}`, {
        version: title,
        status,
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteVersion = createAsyncThunk(
  "projects/deleteVersion",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/v1/version/${id}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const forceDeleteVersion = createAsyncThunk(
  "projects/forceDeleteVersion",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/version/${id}/force`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchVersionComments = createAsyncThunk(
  "projects/fetchVersionComments",
  async ({ versionId, isDeleted = false }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/version/${versionId}/comment?isDeleted=${isDeleted}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createVersionComment = createAsyncThunk(
  "projects/createVersionComments",
  async ({ versionId, commentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/version/${versionId}/comment`,
        commentData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createMediaPresign = createAsyncThunk(
  "projects/createMediaPresign",
  async ({ mediaData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/media/presign`,
        mediaData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const completeMediaUpload = createAsyncThunk(
  "projects/completeMediaUpload",
  async ({ mediaId, mediaData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/api/v1/media/${mediaId}/complete`,
        mediaData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateVersionComment = createAsyncThunk(
  "projects/updateVersionComment",
  async ({ commentId, commentData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/comment/${commentId}`,
        commentData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteVersionComment = createAsyncThunk(
  "projects/deleteVersionComments",
  async ({ versionId, commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/comment/${commentId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const restoreVersionComment = createAsyncThunk(
  "projects/restoreVersionComments",
  async ({ versionId, commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/api/v1/comment/${commentId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const forceDeleteVersionComment = createAsyncThunk(
  "projects/deleteVersionComments",
  async ({ versionId, commentId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/comment/${commentId}/force`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateProject = createAsyncThunk(
  "projects/updateProject",
  async ({ id, projectData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/v1/project/${id}`,
        projectData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Accept: "application/json",
          },
          transformRequest: [(data) => data], // Prevent axios from transforming FormData
        }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const deleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/v1/project/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const forceDeleteProject = createAsyncThunk(
  "projects/deleteProject",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/v1/project/${id}/force`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createTag = createAsyncThunk(
  "tags/createTag",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/v1/tag`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteTag = createAsyncThunk(
  "tags/deleteTag",
  async ({ tagId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/api/v1/tag/${tagId}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchTags = createAsyncThunk(
  "tags/fetchTags",
  async (organizationId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/tag?organizationId=${organizationId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteProjectRole = createAsyncThunk(
  "projects/deleteProjectRole",
  async (roleId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/project-role/${roleId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteVersionRole = createAsyncThunk(
  "projects/deleteVersionRole",
  async ({ roleId, versionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `/api/v1/project-role/${roleId}/version/${versionId}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const resetProjectsExplorer = () => ({
  type: "RESET_PROJECTS_EXPLORER",
  payload: "",
});

export const setCurrentOrganization = (organization) => ({
  type: "SET_CURRENT_ORGANIZATION",
  payload: organization,
});

export const explorerVersionUpdated = (path, isDelete, updatedVersion) => ({
  type: "EXLPORER_VERSION_UPDATED",
  payload: { path, isDelete, updatedVersion },
});

export const explorerProjectUpdated = (path, isDelete, updatedProject) => ({
  type: "EXLPORER_PROJECT_UPDATED",
  payload: { path, isDelete, updatedProject },
});

export const switchProjectUnfold = (id, isUnfolded, path) => ({
  type: "SWITCH_PROJECT_UNFOLD",
  payload: { id, isUnfolded, path },
});

export const updateSharedProjectChildren = (id, childrenObject, path) => ({
  type: "UPDATE_SHARED_PROJECT_CHILDREN",
  payload: { id, childrenObject, path },
});

export const switchSharedProjectUnfold = (id, isUnfolded, path) => ({
  type: "SWITCH_SHARED_PROJECT_UNFOLD",
  payload: { id, isUnfolded, path },
});

export const setSharedProjectsStructure = (newStructure) => ({
  type: "SET_SHARED_PROJECTS_STRUCTURE",
  payload: { newStructure },
});

export const removeOrganizationFromStructure = (id) => ({
  type: "REMOVE_ORGANIZATION_FROM_STRUCTURE",
  payload: { id },
});

const projectsSlice = createSlice({
  name: "projects",
  initialState: {
    items: [],
    itemsArchived: [],
    current: null,
    loading: false,
    error: null,
    currentUser: null,
    currentProjectsStructure: {},
    currentActiveProjectsAmount: 0,
    currentOrganization: null,
    organizationsList: [],
    sharedProjectsArray: [],
    sharedProjectsStructure: {},
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase("SET_CURRENT_ORGANIZATION", (state, action) => {
        state.currentOrganization = action.payload;
      })
      .addCase("SET_SHARED_PROJECTS_STRUCTURE", (state, action) => {
        state.sharedProjectsStructure = action.payload.newStructure;
      })
      .addCase(fetchSharedProjects.fulfilled, (state, action) => {
        state.sharedProjectsArray = action.payload;
        const newSharedProjectsStructure = {};

        for (const item of action.payload) {
          newSharedProjectsStructure[item.id] = {
            id: item.id,
            title: item.version || item.title,
            isFolder: !item.version,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === item.id
            ),
            isUnfolded: false,
            isShared: true,
            isRoot: true,
            orgId: item.organizationId,
            children: {},
          };
        }

        state.sharedProjectsStructure = newSharedProjectsStructure;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        const { isDeleted } = action.meta.arg;
        state.loading = false;
        if (isDeleted) {
          state.itemsArchived = action.payload.filter(
            (proj) => proj.deletionStatus !== "MARKED_FOR_DELETE"
          );
          return;
        }
        const projArray = action.payload.filter(
          (proj) => proj.deletionStatus !== "MARKED_FOR_DELETE"
        );
        state.items = projArray;
        state.currentActiveProjectsAmount = projArray.length;
        const newCurrentProjectsStructure = {};

        for (const project of projArray) {
          newCurrentProjectsStructure[project.id] = {
            id: project.id,
            title: project.title,
            isFolder: true,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === project.id
            ),
            isUnfolded: true,
            isRoot: true,
            isOrg: false,
            children: {},
          };
        }

        let resultProjectsStructure = {};

        for (const organization of state.organizationsList) {
          resultProjectsStructure[organization.id] = {
            id: organization.id,
            title: organization.title,
            isFolder: false,
            isUnfolded: organization.id === state.currentOrganization.id,
            isRoot: false,
            isOrg: true,
            isOwner: !!state.currentUser.organizationRoles.find(
              (org) => org.organizationId === organization.id
            ),
            children:
              organization.id === state.currentOrganization.id
                ? { ...newCurrentProjectsStructure }
                : {},
          };
        }

        state.currentProjectsStructure = resultProjectsStructure;
      })
      .addCase(
        "SWITCH_PROJECT_UNFOLD",
        (state, { payload: { id, isUnfolded, path } }) => {
          let newProjectStructure = { ...state.currentProjectsStructure };
          const pathString = path
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1)
            .slice(0, -9);
          deepSet(
            newProjectStructure,
            `${state.currentOrganization.id}.children.` +
              pathString +
              ".isUnfolded",
            isUnfolded
          );
          state.currentProjectsStructure = newProjectStructure;
        }
      )
      .addCase(
        "SWITCH_SHARED_PROJECT_UNFOLD",
        (state, { payload: { id, isUnfolded, path } }) => {
          let newSharedProjectsStructure = { ...state.sharedProjectsStructure };
          const pathString = path
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1)
            .slice(0, -9);
          deepSet(
            newSharedProjectsStructure,
            pathString + ".isUnfolded",
            isUnfolded
          );
          state.sharedProjectsStructure = newSharedProjectsStructure;
        }
      )
      .addCase(
        "UPDATE_SHARED_PROJECT_CHILDREN",
        (state, { payload: { id, childrenObject, path } }) => {
          let newSharedProjectsStructure = { ...state.sharedProjectsStructure };
          const pathString = path
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1)
            .slice(0, -9);
          deepSet(newSharedProjectsStructure, pathString + ".isUnfolded", true);
          deepSet(
            newSharedProjectsStructure,
            pathString + ".children",
            childrenObject
          );
          state.sharedProjectsStructure = newSharedProjectsStructure;
        }
      )
      .addCase(
        "REMOVE_ORGANIZATION_FROM_STRUCTURE",
        (state, { payload: { id } }) => {
          let newOrganizationsList = [...state.organizationsList];
          newOrganizationsList = newOrganizationsList.filter(
            (org) => org.id !== id
          );

          state.organizationsList = newOrganizationsList;
        }
      )
      .addCase("RESET_PROJECTS_EXPLORER", (state, action) => {
        let newProjectStructure = {};

        for (const project of state.items) {
          newProjectStructure[project.id] = {
            id: project.id,
            title: project.title,
            isFolder: true,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === project.id
            ),
            isRoot: true,
            isUnfolded: true,
            children: {},
          };
        }
        let resultProjectsStructure = {};

        for (const organization of state.organizationsList) {
          resultProjectsStructure[organization.id] = {
            id: organization.id,
            title: organization.title,
            isFolder: false,
            isUnfolded: organization.id === state.currentOrganization.id,
            isRoot: false,
            isOrg: true,
            isOwner: !!state.currentUser.organizationRoles.find(
              (org) => org.organizationId === organization.id
            ),
            children:
              organization.id === state.currentOrganization.id
                ? { ...newProjectStructure }
                : {},
          };
        }
        state.currentProjectsStructure = resultProjectsStructure;
      })
      .addCase(
        "EXLPORER_VERSION_UPDATED",
        (state, { payload: { path, isDelete, updatedVersion } }) => {
          let newProjectStructure = { ...state.currentProjectsStructure };
          if (!isDelete) {
            deepSet(
              newProjectStructure,
              `${state.currentOrganization.id}.children.` + path + ".title",
              updatedVersion.version
            );
            if (updatedVersion.projectId === state.current.id) {
              let versionIndex = state.current.versions.findIndex(
                (version) => version.id === updatedVersion.id
              );
              let newCurrentProject =
                (state.current && { ...state.current }) || {};
              let stateUpdatedVersion = {
                ...newCurrentProject.versions[versionIndex],
                version: updatedVersion.version,
              };
              newCurrentProject.versions[versionIndex] = stateUpdatedVersion;
              state.current = newCurrentProject;
            }
          } else {
            deepDelete(
              newProjectStructure,
              `${state.currentOrganization.id}.children.` + path
            );
            let versionIndex = state.current.versions.findIndex(
              (version) => version.id === updatedVersion.id
            );
            if (versionIndex > -1) {
              let newCurrentProject =
                (state.current && { ...state.current }) || {};
              let stateUpdatedVersions = [...newCurrentProject.versions];
              stateUpdatedVersions.splice(versionIndex, 1);
              newCurrentProject.versions = stateUpdatedVersions;
              state.current = newCurrentProject;
            }
          }

          state.currentProjectsStructure = newProjectStructure;
        }
      )
      .addCase(
        "EXLPORER_PROJECT_UPDATED",
        (state, { payload: { path, isDelete, updatedProject } }) => {
          let newProjectStructure = { ...state.currentProjectsStructure };
          if (!isDelete) {
            deepSet(
              newProjectStructure,
              `${state.currentOrganization.id}.children.` + path + ".title",
              updatedProject.title
            );
            if (state.current) {
              if (updatedProject.id === state.current.id) {
                let newCurrentProject =
                  (state.current && { ...state.current }) || {};
                newCurrentProject = {
                  ...newCurrentProject,
                  title: updatedProject.title,
                };
                state.current = newCurrentProject;
              }
              let subprojectIndex = state.current.subprojects.findIndex(
                (sub) => sub.id === updatedProject.id
              );
              if (subprojectIndex > -1) {
                let newCurrentProject =
                  (state.current && { ...state.current }) || {};
                let stateUpdatedSubproject = {
                  ...newCurrentProject.subprojects[subprojectIndex],
                  title: updatedProject.title,
                  posterUrl: updatedProject.posterUrl,
                };
                newCurrentProject.subprojects[subprojectIndex] =
                  stateUpdatedSubproject;
                state.current = newCurrentProject;
              }
            }
          } else {
            deepDelete(
              newProjectStructure,
              `${state.currentOrganization.id}.children.` + path
            );
            if (state.current) {
              let subprojectIndex = state.current.subprojects.findIndex(
                (sub) => sub.id === updatedProject.id
              );
              if (subprojectIndex > -1) {
                let newCurrentProject =
                  (state.current && { ...state.current }) || {};
                let stateUpdatedSubprojects = [
                  ...newCurrentProject.subprojects,
                ];
                stateUpdatedSubprojects.splice(subprojectIndex, 1);
                newCurrentProject.subprojects = stateUpdatedSubprojects;
                state.current = newCurrentProject;
              }
            }
          }

          state.currentProjectsStructure = newProjectStructure;
        }
      )
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by ID
      .addCase(fetchProjectById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;

        if (action.meta.arg.isLoggedShared) {
          const projectPath = action.payload.path;
          const pathString = projectPath
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1);
          const pathUnfoldedString = projectPath
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1)
            .slice(0, -9);
          let newSharedProjectsStructure = { ...state.sharedProjectsStructure };
          let updatedProject = {};
          const filteredVersionsArray = action.payload.versions.filter(
            (ver) => ver.deletionStatus !== "MARKED_FOR_DELETE"
          );
          for (const version of filteredVersionsArray) {
            updatedProject[version.id] = {
              id: version.id,
              title: version.version,
              isFolder: false,
            };
          }
          for (const subproject of action.payload.subprojects) {
            updatedProject[subproject.id] = {
              id: subproject.id,
              title: subproject.title,
              isFolder: true,
              myRole: state.currentUser.projectRoles.find(
                (role) => role.projectId === subproject.id
              ),
              isUnfolded: true,
              children: {},
            };
          }
          if (!action.meta.arg.showArchived) {
            deepSet(newSharedProjectsStructure, pathString, updatedProject);
            deepSet(
              newSharedProjectsStructure,
              pathUnfoldedString + ".isUnfolded",
              true
            );
          }

          state.sharedProjectsStructure = newSharedProjectsStructure;
          return;
        }
        const projectPath = action.payload.path;
        const pathString = projectPath
          .reduce((acc, curr) => {
            return acc + `.${curr.id}.children`;
          }, "")
          .slice(1);
        let newProjectStructure = { ...state.currentProjectsStructure };
        let updatedProject = {};
        const filteredVersionsArray = action.payload.versions.filter(
          (ver) => ver.deletionStatus !== "MARKED_FOR_DELETE"
        );
        for (const version of filteredVersionsArray) {
          updatedProject[version.id] = {
            id: version.id,
            title: version.version,
            isFolder: false,
          };
        }
        for (const subproject of action.payload.subprojects) {
          updatedProject[subproject.id] = {
            id: subproject.id,
            title: subproject.title,
            isFolder: true,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === subproject.id
            ),
            isUnfolded: true,
            children: {},
          };
        }
        if (!action.meta.arg.showArchived) {
          deepSet(
            newProjectStructure,
            `${state.currentOrganization.id}.children.` + pathString,
            updatedProject
          );
        }

        state.currentProjectsStructure = newProjectStructure;
        const versionsCount =
          (filteredVersionsArray && filteredVersionsArray.length) || 0;
        const subprojectsCount =
          (action.payload.subprojects && action.payload.subprojects.length) ||
          0;
        state.currentActiveProjectsAmount = versionsCount + subprojectsCount;
      })
      .addCase(fetchProjectById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchSharedProjectById.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
        const projectPath = action.payload.path;
        const pathString = projectPath
          .reduce((acc, curr) => {
            return acc + `.${curr.id}.children`;
          }, "")
          .slice(1);
        let newSharedProjectStructure = { ...state.sharedProjectsStructure };
        let updatedProject = {};
        for (const version of action.payload.versions) {
          updatedProject[version.id] = {
            id: version.id,
            title: version.version,
            isFolder: false,
            isShared: true,
          };
        }
        for (const subproject of action.payload.subprojects) {
          updatedProject[subproject.id] = {
            id: subproject.id,
            title: subproject.title,
            isFolder: true,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === subproject.id
            ),
            isUnfolded: true,
            isShared: true,
            children: {},
          };
        }
        deepSet(
          newSharedProjectStructure,
          `${state.currentOrganization.id}.children.` + pathString,
          updatedProject
        );

        state.sharedProjectsStructure = newSharedProjectStructure;
        const versionsCount =
          (action.payload.versions && action.payload.versions.length) || 0;
        const subprojectsCount =
          (action.payload.subprojects && action.payload.subprojects.length) ||
          0;
        state.currentActiveProjectsAmount = versionsCount + subprojectsCount;
      })
      .addCase(fetchProjectByIdForExplorer.fulfilled, (state, action) => {
        if (action.meta.isLoggedShared) {
          const projectPath = action.payload.path;
          const pathString = projectPath
            .reduce((acc, curr) => {
              return acc + `.${curr.id}.children`;
            }, "")
            .slice(1);
          let newSharedProjectsStructure = { ...state.sharedProjectsStructure };
          let updatedProject = {};
          const filteredVersionsArray = action.payload.versions.filter(
            (ver) => ver.deletionStatus !== "MARKED_FOR_DELETE"
          );
          for (const version of filteredVersionsArray) {
            updatedProject[version.id] = {
              id: version.id,
              title: version.version,
              isFolder: false,
            };
          }
          for (const subproject of action.payload.subprojects) {
            updatedProject[subproject.id] = {
              id: subproject.id,
              title: subproject.title,
              isFolder: true,
              myRole: state.currentUser.projectRoles.find(
                (role) => role.projectId === subproject.id
              ),
              isUnfolded: true,
              children: {},
            };
          }
          deepSet(newSharedProjectsStructure, pathString, updatedProject);

          state.sharedProjectsStructure = newSharedProjectsStructure;
          return;
        }

        const projectPath = action.payload.path;
        const pathString = projectPath
          .reduce((acc, curr) => {
            return acc + `.${curr.id}.children`;
          }, "")
          .slice(1);

        let newProjectStructure = { ...state.currentProjectsStructure };
        let updatedProject = {};
        const filteredVersionsArray = action.payload.versions.filter(
          (ver) => ver.deletionStatus !== "MARKED_FOR_DELETE"
        );
        for (const version of filteredVersionsArray) {
          updatedProject[version.id] = {
            id: version.id,
            title: version.version,
            isFolder: false,
          };
        }
        for (const subproject of action.payload.subprojects) {
          updatedProject[subproject.id] = {
            id: subproject.id,
            title: subproject.title,
            isFolder: true,
            myRole: state.currentUser.projectRoles.find(
              (role) => role.projectId === subproject.id
            ),
            isUnfolded: true,
            children: {},
          };
        }
        deepSet(
          newProjectStructure,
          `${state.currentOrganization.id}.children.` + pathString,
          updatedProject
        );

        state.currentProjectsStructure = newProjectStructure;
      })
      // Create
      .addCase(createProject.fulfilled, (state, action) => {
        state.items.push(action.payload);
        const projectPath = action.payload.path;
        const pathString = projectPath
          .reduce((acc, curr) => {
            return acc + `.${curr.id}.children`;
          }, "")
          .slice(1)
          .slice(0, -9);
        let newProjectStructure = { ...state.currentProjectsStructure };
        const newProjectRole = {
          allowedContentVersionIds: [],
          id: "new",
          organizationId: state.currentOrganization.id,
          projectId: action.payload.id,
          role: "ADMIN",
          status: "ACTIVE",
        };
        const newProjectRoles = [
          ...state.currentUser.projectRoles,
          newProjectRole,
        ];
        state.currentUser.projectRoles = newProjectRoles;
        let updatedProject = {
          id: action.payload.id,
          title: action.payload.title,
          isUnfolded: true,
          isFolder: true,
          myRole: newProjectRole,
          isRoot: !pathString.includes("children"),
          children: {},
        };
        deepSet(
          newProjectStructure,
          `${state.currentOrganization.id}.children.` + pathString,
          updatedProject
        );
        state.currentProjectsStructure = newProjectStructure;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        const newOrgList = [...state.organizationsList];

        const orgIndex = newOrgList.findIndex(
          (org) => org.id === action.payload.id
        );

        if (orgIndex > -1) {
          newOrgList[orgIndex].title = action.payload.title;
        }

        if (
          !!state.currentOrganization &&
          action.payload.id === state.currentOrganization.id
        ) {
          state.currentOrganization.title = action.payload.title;
        }

        state.organizationsList = newOrgList;
        const newProjectStructure = {
          ...state.currentProjectsStructure,
          [action.payload.id]: {
            ...state.currentProjectsStructure[action.payload.id],
            title: action.payload.title,
          },
        };
        state.currentProjectsStructure = newProjectStructure;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        const newOrgList = [...state.organizationsList];

        const orgIndex = newOrgList.findIndex(
          (org) => org.id === action.payload
        );

        if (orgIndex > -1) {
          newOrgList.splice(orgIndex, 1);
        }

        state.organizationsList = newOrgList;
        const newProjectStructure = {
          ...state.currentProjectsStructure,
        };
        delete newProjectStructure[action.payload];
        state.currentProjectsStructure = newProjectStructure;
      })
      // Update
      .addCase(updateProject.fulfilled, (state, action) => {
        const idx = state.items.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.items[idx] = action.payload;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.organizationsList = [...state.organizationsList, action.payload];
        state.currentOrganization = action.payload;

        let newProjectStructure = { ...state.currentProjectsStructure };

        newProjectStructure[action.payload.id] = {
          id: action.payload.id,
          title: action.payload.title,
          isFolder: false,
          isUnfolded: true,
          isRoot: false,
          isOrg: true,
          isOwner: !!state.currentUser.organizationRoles.find(
            (org) => org.organizationId === action.payload.id
          ),
          children: {},
        };

        state.currentProjectsStructure = newProjectStructure;
      })
      // Delete
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.items = state.items.filter((p) => p.id !== action.payload);
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        const currentOrganizationId = localStorage.getItem(
          "currentOrganizationId"
        );
        if (currentOrganizationId) {
          const currentOrg = action.payload.organizations.find(
            (org) => org.id === +currentOrganizationId
          );
          if (currentOrg) {
            state.currentOrganization = currentOrg;
          } else {
            state.currentOrganization = action.payload.organizations[0];
          }
        } else {
          state.currentOrganization = action.payload.organizations[0];
        }
        state.organizationsList = action.payload.organizations;
      });
  },
});

export default projectsSlice.reducer;
